import { useState } from "react";
import { useOffice } from "@/office/office-context";
import { cellRefOf, columnLabel, displayCell, isFormula } from "@/lib/sheet-engine";

export function SheetEditor() {
  const office = useOffice();
  const sheet = office.view.sheet;
  const live = !office.replaying;
  const [draft, setDraft] = useState<string | null>(null);

  const activeRef = office.activeCell;
  const activeRaw = sheet.cells[activeRef] ?? "";

  const commit = (ref: string, value: string) => {
    office.setCellValue(ref, value);
    setDraft(null);
  };

  const rowIndexes = Array.from({ length: sheet.rows }, (_, index) => index);

  return (
    <section className="flex h-full min-h-0 flex-col bg-[#f4f2ec]">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-[#e3ded2] bg-white px-3 text-[12px]">
        {/* #region ui.header */}
        <button
          type="button"
          disabled={!live}
          onClick={office.toggleHeaderBold}
          className={`flex h-7 items-center gap-1 rounded px-2 text-[12px] transition-colors ${
            !live
              ? "cursor-not-allowed text-[#c3c7cc]"
              : sheet.headerBold
                ? "bg-[#e8efff] text-[#2b62d9] ring-1 ring-[#c9dbff]"
                : "text-[#3f4a57] hover:bg-[#f1efe9]"
          }`}
        >
          <span className="text-[13px] font-bold leading-none">B</span>
          表头加粗
        </button>
        {/* #endregion ui.header */}
        <span className="mx-1 h-4 w-px bg-[#e2ded4]" />
        {/* #region ui.row */}
        <button
          type="button"
          disabled={!live}
          onClick={office.addRow}
          className={`flex h-7 items-center gap-1 rounded px-2 text-[12px] transition-colors ${
            live ? "text-[#3f4a57] hover:bg-[#f1efe9]" : "cursor-not-allowed text-[#c3c7cc]"
          }`}
        >
          新增行
        </button>
        {/* #endregion ui.row */}
        <span className="ml-auto text-[11px] text-[#9a9388]">支持 =SUM(B2:B3)、=B2*2、=AVERAGE(B2:B4)</span>
      </div>

      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-[#e3ded2] bg-[#fbfaf7] px-3 text-[12px]">
        <span className="w-10 rounded border border-[#e3ded2] bg-white px-1 text-center font-mono text-[11px] text-[#5d6672]">{activeRef}</span>
        <span className="font-serif text-[12px] italic text-[#9a9388]">fx</span>
        <input
          value={draft ?? activeRaw}
          disabled={!live}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => setDraft(activeRaw)}
          onBlur={() => commit(activeRef, draft ?? activeRaw)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commit(activeRef, draft ?? activeRaw);
              event.currentTarget.blur();
            }
            if (event.key === "Escape") setDraft(null);
          }}
          className="h-6 min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 font-mono text-[12px] text-[#333a42] outline-none focus:border-[#c9dbff] focus:bg-white"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-5">
        <div className="overflow-hidden rounded-md border border-[#e0dbcf] bg-white shadow-[0_1px_2px_rgba(31,35,40,0.06)]">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-[#f7f5f0]">
                <th className="w-9 border-b border-r border-[#e6e2d8] py-1.5 text-[10px] font-normal text-[#a8a094]" />
                {Array.from({ length: sheet.cols }, (_, col) => (
                  <th key={col} className="border-b border-r border-[#e6e2d8] py-1.5 text-[11px] font-medium text-[#8d8679] last:border-r-0">
                    {columnLabel(col)}
                  </th>
                ))}
              </tr>
            </thead>
            {/* #region ui.grid */}
            <tbody>
              {rowIndexes.map((row) => (
                <tr key={row}>
                  <td className="border-b border-r border-[#efebe2] bg-[#faf8f3] py-1 text-center text-[10px] text-[#a8a094]">{row + 1}</td>
                  {Array.from({ length: sheet.cols }, (_, col) => {
                    const ref = cellRefOf(col, row);
                    const selected = ref === activeRef;
                    return (
                      <td
                        key={ref}
                        onClick={() => live && office.selectCell(ref)}
                        className={`h-8 border-b border-r border-[#efebe2] p-0 last:border-r-0 ${
                          selected ? "bg-[#f4f8ff] ring-1 ring-inset ring-[#4f8cff]" : "hover:bg-[#faf9f5]"
                        }`}
                      >
                        {selected && live ? (
                          <input
                            autoFocus
                            value={draft ?? activeRaw}
                            onChange={(event) => setDraft(event.target.value)}
                            onBlur={() => commit(ref, draft ?? activeRaw)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                commit(ref, draft ?? activeRaw);
                                event.currentTarget.blur();
                              }
                              if (event.key === "Escape") setDraft(null);
                            }}
                            className="h-8 w-full bg-white px-2 font-mono text-[12.5px] text-[#1f2933] outline-none"
                          />
                        ) : (
                          <div
                            className={`flex h-8 items-center px-2 ${
                              isFormula(sheet.cells, ref) ? "font-mono text-[12px] text-[#1f6f4a]" : "text-[#333a42]"
                            } ${row === 0 && sheet.headerBold ? "font-semibold" : ""}`}
                          >
                            <span className="truncate">{displayCell(sheet.cells, ref) || "\u00a0"}</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* #endregion ui.grid */}
          </table>
        </div>
        <p className="mt-3 text-[11px] text-[#a8a094]">单击选中单元格，直接输入内容或公式，回车提交并记录为一步。</p>
      </div>

      <div className="flex h-8 shrink-0 items-center gap-4 border-t border-[#e3ded2] bg-[#fbfaf7] px-4 text-[11px] text-[#8d8679]">
        <span>{sheet.name}.xlsx</span>
        <span>
          {sheet.rows} 行 × {sheet.cols} 列
        </span>
        <span className="ml-auto">{live ? "公式实时计算" : "回放快照 · 只读"}</span>
      </div>
    </section>
  );
}
